import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { isTokenExpired, getTokenAuthorities } from "../utils/tokenUtils";
import { clearAll as clearConsultSuppression } from "../utils/consultSuppression";
import { RootState } from "./index";
import { MarketplaceResponse } from "../types/shared";

type userInfoType = {
  id: string;
  username: string;
  firstName: string;
  secondName: string;
  roles: string[];
};

export interface AuthState {
  token: string | null;
  loginError: boolean;
  loadingLogin: boolean;
  loginServerError: boolean;

  userInfo: userInfoType | null;
  loadingUserInfo: boolean;
  userInfoError: boolean;

  marketplaceInfo: any;
  loadingMarketplaceInfo: boolean;
  marketplaceInfoError: boolean;

  marketplaceInfoDetailed: MarketplaceResponse | null;
  loadingMarketplaceInfoDetailed: boolean;
  marketplaceInfoDetailedError: boolean;

  loadingForgetPassword: boolean;
  forgetPasswordError: boolean;

  isAdmin: boolean;
  isPharmacist: boolean;
  isDriver: boolean;
  isInitialLogin: boolean;
  authInitialized: boolean;
  isMarketplaceAdmin: boolean;
}

const initialState: AuthState = {
  token: null,
  loginError: false,
  loadingLogin: false,
  loginServerError: false,

  userInfo: {
    id: "",
    username: "",
    firstName: "",
    secondName: "",
    roles: [],
  },
  loadingUserInfo: false,
  userInfoError: false,

  marketplaceInfo: null,
  loadingMarketplaceInfo: false,
  marketplaceInfoError: false,

  marketplaceInfoDetailed: null,
  loadingMarketplaceInfoDetailed: false,
  marketplaceInfoDetailedError: false,

  loadingForgetPassword: false,
  forgetPasswordError: false,

  isAdmin: false,
  isMarketplaceAdmin: false,
  isPharmacist: false,
  isDriver: false,
  isInitialLogin: false,
  authInitialized: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState: initialState,
  reducers: {
    loginStart: (state) => {
      state.loadingLogin = true;
    },
    userInfoFn: (state, action: PayloadAction<userInfoType | null>) => {
      state.userInfo = action.payload;
      state.loadingUserInfo = false;
      state.userInfoError = false;
    },
    fetchUserInfoStart: (state) => {
      state.loadingUserInfo = true;
      state.userInfoError = false;
    },
    fetchUserInfoFailed: (state) => {
      state.loadingUserInfo = false;
      state.loadingLogin = false;
      state.userInfoError = true;
    },

    loginSuccess: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
      state.loadingLogin = false;
      state.loginError = false;
    },
    loginFailed: (state) => {
      state.loadingLogin = false;
      state.loginError = true;
    },
    loginStatusReset: (state) => {
      state.loginError = false;
      state.loadingLogin = false;
      state.loginServerError = false;
    },
    logout: (state) => {
      // Clear all state first
      state.token = null;
      state.marketplaceInfo = null;
      state.marketplaceInfoDetailed = null;
      state.userInfo = {
        id: "",
        username: "",
        firstName: "",
        secondName: "",
        roles: [],
      };

      // Reset all role flags
      state.isAdmin = false;
      state.isMarketplaceAdmin = false;
      state.isPharmacist = false;
      state.isDriver = false;
      state.isInitialLogin = false;

      // Reset loading and error states
      state.loginError = false;
      state.loadingLogin = false;

      // Keep authInitialized true for subsequent logins
      state.authInitialized = true;

      // Clear localStorage after state
      clearLocalStorage();

      console.log("Auth: Complete logout performed");
    },
    fetchMarketplaceInfoStart: (state) => {
      state.loadingMarketplaceInfo = true;
      state.marketplaceInfoError = false;
    },
    marketplaceInfo: (state, action) => {
      state.marketplaceInfo = action.payload;
      state.loadingMarketplaceInfo = false;
      state.marketplaceInfoError = false;
    },
    fetchMarketplaceInfoFailed: (state, action) => {
      state.marketplaceInfoError = action.payload;
      state.loadingMarketplaceInfo = false;
      state.loadingLogin = false;
      state.marketplaceInfoError = true;
    },
    loginServerError: (state) => {
      state.loginError = false;
      state.loginServerError = true;
      state.loadingLogin = false;
    },
    setActiveStatus: (state, action) => {
      if (state.marketplaceInfo) {
        state.marketplaceInfo = {
          ...state.marketplaceInfo,
          takesOrders: action.payload,
        };
      }
    },
    fetchMarketplaceInfoDetailedStart: (state) => {
      state.loadingMarketplaceInfoDetailed = true;
      state.marketplaceInfoDetailedError = false;
    },
    setMarketplaceInfoDetailed: (
      state,
      action: PayloadAction<MarketplaceResponse>
    ) => {
      state.marketplaceInfoDetailed = action.payload;
      state.loadingMarketplaceInfoDetailed = false;
      state.marketplaceInfoDetailedError = false;
    },
    fetchMarketplaceInfoDetailedFailed: (state) => {
      state.loadingMarketplaceInfoDetailed = false;
      state.marketplaceInfoDetailedError = true;
    },
    setIsMarketplaceAdmin: (state) => {
      state.isMarketplaceAdmin = true;
      state.isAdmin = false;
      state.isPharmacist = false;
      state.isDriver = false;
    },
    setIsAdmin: (state) => {
      state.isAdmin = true;
      state.isMarketplaceAdmin = false;
      state.isPharmacist = false;
      state.isDriver = false;
    },
    setIsPharmacist: (state) => {
      state.isAdmin = false;
      state.isMarketplaceAdmin = false;
      state.isPharmacist = true;
      state.isDriver = false;
    },
    setIsDriver: (state) => {
      state.isDriver = true;
      state.isAdmin = false;
      state.isMarketplaceAdmin = false;
      state.isPharmacist = false;
    },
    setRegularUser: (state) => {
      state.isAdmin = false;
      state.isMarketplaceAdmin = false;
      state.isPharmacist = false;
      state.isDriver = false;
    },
    setIsInitialLogin: (state, action: PayloadAction<boolean>) => {
      state.isInitialLogin = action.payload;
    },
    setAuthInitialized: (state, action: PayloadAction<boolean>) => {
      state.authInitialized = action.payload;
    },
  },
});

const clearLocalStorage = () => {
  // Clear all authentication-related keys
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("expirationDate");
  localStorage.removeItem("email");
  localStorage.removeItem("id");
  localStorage.removeItem("firstName");
  localStorage.removeItem("secondName");
  localStorage.removeItem("roles");

  // Clear marketplace IDs
  localStorage.removeItem("marketplaceId");
  localStorage.removeItem("branchMarketplaceId");

  // Video-consult PiP: drop the joined-consult suppression markers. Must be
  // synchronous — the 401/403 interceptor hard-redirects on the line after
  // its logout dispatch, so cleanup after a task-queue hop would be lost.
  clearConsultSuppression();

  // Clear any other potential cached marketplace data
  // You might need to add other keys if you discover them
  localStorage.removeItem("selectedMarketplace");
  localStorage.removeItem("marketplaceCache");

  // Log cleanup for debugging
  console.log("Auth: localStorage cleared completely");

  // Optional - more aggressive cleanup of all apoteka-related keys
  // Uncomment if you want to clear ALL localStorage keys that might be related
  /*
  Object.keys(localStorage).forEach(key => {
    if (key.includes('marketplace') || key.includes('branch') || key.includes('apoteka')) {
      localStorage.removeItem(key);
      console.log(`Cleared additional localStorage key: ${key}`);
    }
  });
  */
};

export const authActions = authSlice.actions;

// Memoized selector for checking if user is authenticated with valid token
export const selectIsAuthenticated = createSelector(
  [
    (state: RootState) => state.auth.token,
    (state: RootState) => state.auth.authInitialized,
  ],
  (token, isInitialized) => {
    // Return false if no token or auth not initialized
    if (!token || !isInitialized) {
      return false;
    }

    // Validate token expiration
    try {
      return !isTokenExpired(token);
    } catch {
      console.warn("Auth: Invalid token format during validation");
      return false;
    }
  }
);

// Extract user roles from token using robust tokenUtils
export const selectUserRoles = createSelector(
  [selectIsAuthenticated, (state: RootState) => state.auth.token],
  (isAuthenticated, token) => {
    if (!isAuthenticated || !token) {
      return [];
    }
    return getTokenAuthorities(token);
  }
);

export const selectCanViewTransactions = createSelector(
  [selectUserRoles],
  (roles) => roles.includes("PRODAJNO_MESTO_USER")
);

// Enhanced role selectors that check both Redux state and token roles
export const selectIsAdmin = createSelector(
  [selectUserRoles, (state: RootState) => state.auth.isAdmin],
  (roles, isAdminFlag) => {
    return isAdminFlag || roles.includes("ADMIN");
  }
);

export const selectIsMarketplaceAdmin = createSelector(
  [selectUserRoles, (state: RootState) => state.auth.isMarketplaceAdmin],
  (roles, flag) => flag || roles.includes("MARKETPLACE_ADMIN_USER")
);

export const selectCanViewAdminMarketplace = createSelector(
  [selectIsMarketplaceAdmin],
  (isMarketplaceAdmin) => isMarketplaceAdmin
);

export const selectIsPharmacist = createSelector(
  [selectUserRoles, (state: RootState) => state.auth.isPharmacist],
  (roles, isPharmacistFlag) => {
    return (
      isPharmacistFlag ||
      roles.includes("PHARMACIST_SUPPORT_USER") ||
      roles.includes("PHARMACIST")
    );
  }
);



export const selectIsDriver = createSelector(
  [selectUserRoles, (state: RootState) => state.auth.isDriver],
  (roles, isDriverFlag) => {
    return isDriverFlag || roles.includes("DELIVERY_USER");
  }
);

// Determine user type based on roles
export const selectUserType = createSelector(
  [selectIsAdmin, selectIsPharmacist, selectIsDriver, selectIsMarketplaceAdmin],
  (isAdmin, isPharmacist, isDriver, isMarketplaceAdmin) => {
    if (isAdmin) return "admin";
    if (isMarketplaceAdmin) return "marketplaceAdmin";
    if (isPharmacist) return "pharmacist";
    if (isDriver) return "driver";
    return "marketplace";
  }
);

// Comprehensive auth state selector
export const selectAuthState = createSelector(
  [
    selectIsAuthenticated,
    selectIsAdmin,
    selectIsPharmacist,
    selectIsDriver,
    selectUserType,
    selectUserRoles,
  ],
  (isAuthenticated, isAdmin, isPharmacist, isDriver, userType, roles) => ({
    isAuthenticated,
    isAdmin,
    isPharmacist,
    isDriver,
    userType,
    roles,
    canAccessAdminArea: isAuthenticated && isAdmin,
    canAccessPharmacyArea: isAuthenticated && (isPharmacist || isAdmin),
    canAccessDriverArea: isAuthenticated && (isDriver || isAdmin),
  })
);

// Selector for detailed marketplace info
export const selectMarketplaceDetailed = (state: RootState) =>
  state.auth.marketplaceInfoDetailed;

// Selector for active pharmacist license
export const selectActiveLicense = createSelector(
  [selectMarketplaceDetailed],
  (detailed) => detailed?.marketplacePharmacyLicenseResponse ?? null
);

export default authSlice;
